import React from 'react'
import styled from 'styled-components'

import {
  zoning_indice_sources,
  zoning_sources_agreement,
  ZoningMetricSources,
} from '@popety-io/types'
import { formatMetricValue, getSourceColor } from '../utils/zoningHelpers'
import { MinMaxValue } from './MinMaxLabel'
import {
  MetricLabel,
  MetricValueCell,
  EmptyValue,
  DifferenceCell,
  AuthorizedValue,
  AuthorizedValueWithBadge,
} from './ZoneMetricsTable.styles'
import { EditableMinMaxInput } from './EditableMinMaxInput'
import { DualSourceBadgeLine } from './DualSourceBadgeLine'
import { Icon } from '../../../atoms'

const MetricRow = styled.tr<{ $selected?: boolean; $editMode?: boolean }>`
  background-color: ${({ $selected, $editMode, theme }) =>
    $selected && $editMode
      ? theme.colors.primaryLight || 'rgba(0, 122, 255, 0.05)'
      : 'transparent'};

  td {
    border-bottom: none; // Parent metrics have no border (grouped with child)
  }

  &:hover {
    background-color: ${({ theme }) =>
      theme.colors.background.secondary || 'rgba(0, 0, 0, 0.02)'};
  }
`

const MetricSelector = styled.div<{ $visible: boolean }>`
  display: ${({ $visible }) => ($visible ? 'inline-flex' : 'none')};
  align-items: center;
  margin-right: ${({ theme }) => theme.spacing.small};

  input[type='radio'] {
    cursor: pointer;
    width: 16px;
    height: 16px;
  }
`

const MetricNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding-top: ${({ theme }) => theme.spacing.small};
`

const GroupSubNote = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-top: ${({ theme }) => theme.spacing.xsmall};
`

export interface MetricGroupRowProps {
  metricKey: string
  metricLabel: string
  metricDescription?: string
  /** Small note under the label (envelope lands: reference/manual hint) */
  subNote?: string
  groupName: 'group1' | 'group2'
  isSelected: boolean
  isEditing: boolean

  // Values
  currentValue?: number
  minValue?: number
  maxValue?: number

  // Sources (legacy single-bundle — used for single-value metrics)
  minSource?: string
  maxSource?: string
  minReference?: any
  maxReference?: any
  defaultSource?: zoning_indice_sources
  agreement?: zoning_sources_agreement
  sources?: ZoningMetricSources
  minDefaultSource?: zoning_indice_sources
  maxDefaultSource?: zoning_indice_sources
  // Sources (per-bound — used for range metrics when bounds may differ)
  minSources?: ZoningMetricSources
  maxSources?: ZoningMetricSources
  minAgreement?: zoning_sources_agreement
  maxAgreement?: zoning_sources_agreement

  // State
  isOverridden: boolean
  isPending: boolean
  isMinOverridden?: boolean
  isMinPending?: boolean
  unit?: string

  // Handlers
  onGroupChange: (group: 'group1' | 'group2', value: string) => void
  onValueChange: (metricKey: string, value: number) => void
  onValueClear: (metricKey: string) => void
  onAuthorizedClick: (metricKey: string) => void
  status?: 'found' | 'not_found' | 'not_applicable' | 'reference'
}

/**
 * Row component for exclusive metric groups (IOS/COS, IUS/CUS/IBUS/IM)
 * Shows radio selector in edit mode, clickable authorized value
 */
export const MetricGroupRow: React.FC<MetricGroupRowProps> = ({
  metricKey,
  metricLabel,
  // metricDescription,
  subNote,
  groupName,
  isSelected,
  isEditing,
  currentValue,
  minValue,
  maxValue,
  // minSource,
  maxSource,
  // minReference,
  maxReference,
  defaultSource,
  agreement,
  sources,
  minDefaultSource,
  maxDefaultSource,
  minSources,
  maxSources,
  minAgreement,
  maxAgreement,
  isOverridden,
  isPending,
  isMinOverridden = false,
  isMinPending = false,
  unit,
  onGroupChange,
  onValueChange,
  onValueClear,
  onAuthorizedClick,
  status,
}) => {
  // Calculate difference
  const calculateDifference = (): {
    value: number
    isPositive: boolean
  } | null => {
    if (!maxValue) {
      return null
    }

    if (maxValue === undefined || currentValue === undefined) {
      return null
    }

    const diff = maxValue - currentValue

    return { value: diff, isPositive: diff >= 0 }
  }

  const diff = calculateDifference()

  // Format authorized value with min/max labels
  const hasMinMax = minValue !== undefined && maxValue !== undefined
  const hasMax = maxValue !== undefined
  const hasMin = minValue !== undefined
  const isNA =
    status === 'not_applicable' && !isEditing && !minValue && !maxValue

  const authorizedContent = isNA ? (
    <>N/A</>
  ) : hasMinMax ? (
    <MinMaxValue
      minDisplay={formatMetricValue(minValue!, unit, metricKey)}
      maxDisplay={formatMetricValue(maxValue!, unit, metricKey)}
    />
  ) : hasMax ? (
    <MinMaxValue maxDisplay={formatMetricValue(maxValue!, unit, metricKey)} />
  ) : hasMin ? (
    <MinMaxValue minDisplay={formatMetricValue(minValue!, unit, metricKey)} />
  ) : defaultSource ? (
    <>N/A</>
  ) : null
  const isAnyOverridden = isOverridden || isMinOverridden
  const isAnyPending = isPending || isMinPending
  const sourceColor = getSourceColor(maxSource, isAnyOverridden)

  return (
    <MetricRow $selected={isSelected} $editMode={isEditing}>
      <td>
        <MetricNameContainer>
          <MetricSelector $visible={isEditing}>
            <input
              type="radio"
              name={`metric-${groupName}`}
              value={metricKey}
              checked={isSelected}
              onChange={(e) => onGroupChange(groupName, e.target.value)}
            />
          </MetricSelector>
          <MetricLabel>{metricLabel}</MetricLabel>
        </MetricNameContainer>
        {subNote && <GroupSubNote>{subNote}</GroupSubNote>}
      </td>

      <MetricValueCell>
        {currentValue !== undefined ? (
          formatMetricValue(currentValue, unit, metricKey)
        ) : (
          <EmptyValue>-</EmptyValue>
        )}
      </MetricValueCell>

      <MetricValueCell>
        {isEditing ? (
          <EditableMinMaxInput
            metricKey={metricKey}
            minValue={minValue}
            maxValue={maxValue}
            isMinOverridden={isMinOverridden}
            isMaxOverridden={isOverridden}
            isMinPending={isMinPending}
            isMaxPending={isPending}
            minSource={minDefaultSource}
            maxSource={maxDefaultSource}
            onValueChange={onValueChange}
            onValueClear={onValueClear}
          />
        ) : authorizedContent || maxReference || defaultSource ? (
          <AuthorizedValueWithBadge>
            <AuthorizedValue
              $source={maxSource}
              $isUserEdited={isAnyOverridden}
              $isPending={isAnyPending}
              onClick={() => onAuthorizedClick(metricKey)}
              style={{ cursor: 'pointer', color: sourceColor }}
              title="Click to view source details"
            >
              {authorizedContent || <Icon name="alert-circle" size="small" />}
            </AuthorizedValue>
            <DualSourceBadgeLine
              metricKey={metricKey}
              isOverridden={isAnyOverridden}
              defaultSource={defaultSource}
              agreement={agreement}
              sources={sources}
              minDefaultSource={minDefaultSource}
              maxDefaultSource={maxDefaultSource}
              minAgreement={minAgreement}
              maxAgreement={maxAgreement}
              minSources={minSources}
              maxSources={maxSources}
              hasMinMax={hasMinMax}
            />
          </AuthorizedValueWithBadge>
        ) : (
          <EmptyValue>-</EmptyValue>
        )}
      </MetricValueCell>

      <DifferenceCell $isPositive={diff?.isPositive ?? true}>
        {diff ? (
          <>
            {diff.isPositive && '+'}
            {formatMetricValue(diff.value, unit, metricKey)}
          </>
        ) : (
          <EmptyValue>-</EmptyValue>
        )}
      </DifferenceCell>
    </MetricRow>
  )
}
